import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Seo } from "../components/site/Seo";
import { SectionLabel } from "../components/site/SectionLabel";
import { ArticleBlocks } from "../components/site/ArticleBlocks";
import { NewsletterForm } from "../components/site/NewsletterForm";
import { getInsight } from "../data/insights";
import { getAdvisory } from "../data/advisory";
import NotFound from "./NotFound";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function InsightDetail() {
  const { slug } = useParams();
  const a = getInsight(slug);
  if (!a) return <NotFound />;
  const rel = a.related || {};
  const relatedInsights = (rel.insights || []).map(getInsight).filter(Boolean);
  const advisory = rel.advisory ? getAdvisory(rel.advisory) : null;

  return (
    <div>
      <Seo
        title={`${a.title} — MATRIVON Insights`}
        description={a.excerpt}
        path={`/insights/${a.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.excerpt,
          datePublished: a.published,
          dateModified: a.reviewed,
          articleSection: a.category,
          author: { "@type": "Organization", name: "MATRIVON" },
          publisher: { "@type": "Organization", name: "MATRIVON" },
        }}
      />
      <PageHero
        variant="light"
        label={a.category}
        title={a.title}
        crumbs={[{ label: "Insights", to: "/insights" }, { label: a.category }]}
      />

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <article>
            <div className="mb-8 flex flex-wrap gap-4 border-b border-brand-line pb-6 text-xs text-brand-muted">
              <span>Published: {fmtDate(a.published)}</span>
              <span>Last reviewed: {fmtDate(a.reviewed)}</span>
              <span>{a.readMins} min read</span>
            </div>
            <ArticleBlocks blocks={a.blocks} />
          </article>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            {advisory && (
              <div className="border border-brand-blue/20 bg-brand-blue/5 p-6">
                <SectionLabel>Relevant advisory</SectionLabel>
                <h3 className="font-heading text-lg font-bold text-brand-ink">{advisory.title}</h3>
                <p className="mt-2 text-sm text-brand-muted">{advisory.short}</p>
                <Link
                  to={`/advisory/${advisory.slug}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5"
                >
                  Explore advisory <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            {relatedInsights.length > 0 && (
              <div className="border border-brand-line bg-white p-6">
                <SectionLabel>Related insights</SectionLabel>
                <ul className="space-y-3">
                  {relatedInsights.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/insights/${r.slug}`}
                        className="group flex items-start gap-2 text-sm font-medium text-brand-ink hover:text-brand-blue"
                      >
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border border-brand-line bg-brand-ink p-6 text-white">
              <SectionLabel light>Subscribe</SectionLabel>
              <p className="mb-4 text-sm text-white/60">Get new insights and tool reviews.</p>
              <NewsletterForm source="insight" dark />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
